import React, {useState, useEffect, useMemo} from 'react'
import {Box, Text, Button, Flex, Stack, Select, Image, Spinner} from '@chakra-ui/react'
import {useLocation, useHistory} from 'react-router-dom'
import {useCustomQuery} from '@salesforce/commerce-sdk-react'
import {
    clearButtonStyle,
    dropdownBoxSmallStyle,
    dropdownBoxStyle,
    headerTextStyle,
    monthActiveStyle,
    monthStyle,
    paginationButtonActiveStyle,
    paginationButtonStyle,
    secondRowStyle,
    selectStyle,
    yearActiveStyle,
    yearStyle
} from './index.style'
import {useIntl} from 'react-intl'
import Ticket from './partials/ticket'
import colors from '../../../theme/foundations/colors'
import {pxToRem} from '../../../utills/pxToRem'
import {MONTH_NAMES_FULL, MONTH_NUMBERS} from '../../../constants'
import PropTypes from 'prop-types'

const BlogArchive = ({onFilterChange}) => {
    const [activePage, setActivePage] = useState(0)
    const [sortRule, setSortRule] = useState('newest')
    const [pageSize, setPageSize] = useState(10)
    const [selectedYear, setSelectedYear] = useState('')
    const [selectedMonth, setSelectedMonth] = useState('')
    const [expandedYears, setExpandedYears] = useState(new Set())
    const location = useLocation()
    const history = useHistory()
    const intl = useIntl()

    const searchParams = new URLSearchParams(location.search)

    const getMonthNumber = (monthName) => {
        return MONTH_NUMBERS[monthName] || monthName
    }

    const getMonthName = (monthNumber) => {
        return MONTH_NAMES_FULL[monthNumber] || monthNumber
    }

    const sortOptions = [
        {
            value: 'newest',
            label: intl.formatMessage({
                id: 'bloglist.sort.newest',
                defaultMessage: 'Sort By: Newest'
            })
        },
        {
            value: 'oldest',
            label: intl.formatMessage({
                id: 'bloglist.sort.oldest',
                defaultMessage: 'Sort By: Oldest'
            })
        },
        {
            value: 'name-ascending',
            label: intl.formatMessage({
                id: 'bloglist.sort.name_ascending',
                defaultMessage: 'Sort By: Name Ascending'
            })
        },
        {
            value: 'name-descending',
            label: intl.formatMessage({
                id: 'bloglist.sort.name_descending',
                defaultMessage: 'Sort By: Descending'
            })
        }
    ]

    const pageOptions = [
        {
            value: '10',
            label: intl.formatMessage({
                id: 'bloglist.pagination.ten_per_page',
                defaultMessage: '10 per page'
            })
        },
        {
            value: '30',
            label: intl.formatMessage({
                id: 'bloglist.pagination.thirty_per_page',
                defaultMessage: '30 per page'
            })
        }
    ]

    useEffect(() => {
        const yearParam = searchParams.get('year')
        const monthParam = searchParams.get('month')
        const startParam = searchParams.get('start')
        const szParam = searchParams.get('sz')

        if (yearParam) {
            setSelectedYear(yearParam)
        } else {
            setSelectedYear('')
        }

        if (monthParam) {
            if (monthParam.includes('-')) {
                const [year, monthNumber] = monthParam.split('-')
                const monthName = getMonthName(parseInt(monthNumber))
                setSelectedMonth(monthName)
                setSelectedYear(year)
            } else {
                setSelectedMonth(monthParam)
                const yearFromMonth = monthParam.split('-')[0]
                if (yearFromMonth) {
                    setSelectedYear(yearFromMonth)
                }
            }
        } else {
            setSelectedMonth('')
        }

        if (startParam && szParam) {
            const start = parseInt(startParam)
            const size = parseInt(szParam)
            setPageSize(size)
            setActivePage(Math.floor(start / size))
        } else {
            setActivePage(0)
        }
    }, [location.search])

    const updateURLParams = (year, month) => {
        const newParams = new URLSearchParams(location.search)

        if (year) {
            newParams.set('year', year)
        } else {
            newParams.delete('year')
        }

        if (month) {
            newParams.set('month', month)
        } else {
            newParams.delete('month')
        }

        history.push({
            pathname: location.pathname,
            search: newParams.toString()
        })
    }

    const updatePaginationURL = (page, size) => {
        const newParams = new URLSearchParams(location.search)

        if (page > 0) {
            newParams.set('start', page * size)
            newParams.set('sz', size)
        } else {
            newParams.delete('start')
            newParams.delete('sz')
        }

        history.push({
            pathname: location.pathname,
            search: newParams.toString()
        })
    }

    const blogParams = useMemo(() => {
        const params = {}
        params.c_folderId = 'archive'
        if (pageSize * activePage) params.c_start = pageSize * activePage
        if (pageSize) params.c_size = pageSize
        if (sortRule) params.c_srule = sortRule
        if (selectedMonth) params.c_blogMonth = selectedMonth
        if (selectedYear) params.c_blogYear = selectedYear
        return params
    }, ['archive', pageSize * activePage, pageSize, sortRule, selectedMonth, selectedYear])

    const {
        data: response,
        isLoading: loading,
        error
    } = useCustomQuery({
        options: {
            method: 'GET',
            customApiPathParameters: {
                apiName: 'get-content',
                apiVersion: 'v1',
                endpointPath: 'getBlogArticles'
            },
            parameters: blogParams
        }
    })

    const {archiveMenu, articles, maxPages = 10} = response?.data || {}

    const arrowVisible = maxPages > 3

    const getVisiblePages = () => {
        const maxVisible = 3
        let start = Math.max(0, activePage - 1)
        let end = Math.min(maxPages, start + maxVisible)
        if (end - start < maxVisible) {
            start = Math.max(0, end - maxVisible)
        }
        return Array.from({length: end - start}, (_, i) => start + i)
    }

    const visiblePages = getVisiblePages()

    const toggleYearExpansion = (year) => {
        const newExpandedYears = new Set(expandedYears)
        if (newExpandedYears.has(year)) {
            newExpandedYears.delete(year)
        } else {
            newExpandedYears.add(year)
        }
        setExpandedYears(newExpandedYears)
    }

    useEffect(() => {
        window.scrollTo({top: 0, behavior: 'smooth'})
    }, [selectedYear, selectedMonth, activePage])

    useEffect(() => {
        if (onFilterChange) {
            onFilterChange({selectedYear, selectedMonth})
        }
    }, [selectedYear, selectedMonth, onFilterChange])

    if (loading && !error) {
        return (
            <>
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        zIndex: '99999999',
                        height: '100vh',
                        width: '100vw',
                        background: 'rgba(0, 0, 0, 0.4)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <Spinner
                        data-testid="loading"
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="gray.200"
                        color="blue.600"
                        size="xl"
                    />
                </Box>
                <Box minHeight={'700px'}></Box>
            </>
        )
    }

    return (
        !loading && (
            <Stack
                px={pxToRem(20)}
                pb={{base: pxToRem(50), md: pxToRem(70), lg: pxToRem(20)}}
                spacing={{xl: pxToRem(25)}}
            >
                <Text sx={{...headerTextStyle, mb: pxToRem(-1)}} textAlign="center">
                    {intl.formatMessage({
                        id: 'bloglist.archive',
                        defaultMessage: 'Archive'
                    })}
                </Text>
                {/* Mobile Year View */}
                <Box display={{base: 'block', lg: 'none'}} sx={secondRowStyle}>
                    <Button
                        sx={{...clearButtonStyle, mb: pxToRem(15)}}
                        onClick={() => {
                            setSelectedYear('')
                            setSelectedMonth('')
                            setActivePage(0)
                            history.push({
                                pathname: location.pathname,
                                search: ''
                            })
                        }}
                    >
                        {intl.formatMessage({
                            id: 'bloglist.clear_date_filter',
                            defaultMessage: `Clear Date Filter`
                        })}
                    </Button>
                    {Object.keys(archiveMenu || {})
                        .sort((a, b) => b - a)
                        .map((year, index) => (
                            <Box key={year}>
                                <Flex
                                    flexDir="column"
                                    justify="space-between"
                                    sx={{minH: '60px', h: '60px'}}
                                >
                                    <Button
                                        variant="ghost"
                                        sx={
                                            selectedYear === year && selectedMonth === ''
                                                ? yearActiveStyle
                                                : yearStyle
                                        }
                                        borderTop={
                                            index &&
                                            `${pxToRem(1)} solid ${colors.cav_pdp_light_gray}`
                                        }
                                        borderBottom={{
                                            base: 'none',
                                            lg: `${pxToRem(1)} solid ${colors.cav_pdp_light_gray}`
                                        }}
                                        onClick={() => {
                                            setSelectedYear(year)
                                            setSelectedMonth('')
                                            setActivePage(0)
                                            const newParams = new URLSearchParams()
                                            newParams.set('year', year)
                                            history.push({
                                                pathname: location.pathname,
                                                search: newParams.toString()
                                            })
                                        }}
                                    >
                                        {year}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        sx={{
                                            minH: '18px',
                                            h: '18px',
                                            p: 0,
                                            _focus: {
                                                bg: 'cav_light_gray_bg',
                                                borderRadius: 0
                                            }
                                        }}
                                        onClick={() => toggleYearExpansion(year)}
                                    >
                                        <Image
                                            src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAJCAQAAACP6lQKAAAACXBIWXMAAAsTAAALEwEAmpwYAAAF0WlUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPD94cGFja2V0IGJlZ2luPSLvu78iIGlkPSJXNU0wTXBDZWhpSHpyZVN6TlRjemtjOWQiPz4gPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyIgeDp4bXB0az0iQWRvYmUgWE1QIENvcmUgNS42LWMxNDUgNzkuMTYzNDk5LCAyMDE4LzA4LzEzLTE2OjQwOjIyICAgICAgICAiPiA8cmRmOlJERiB4bWxuczpyZGY9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkvMDIvMjItcmRmLXN5bnRheC1ucyMiPiA8cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0iIiB4bWxuczp4bXA9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC8iIHhtbG5zOnhtcE1NPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvbW0vIiB4bWxuczpzdEV2dD0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL3NUeXBlL1Jlc291cmNlRXZlbnQjIiB4bWxuczpkYz0iaHR0cDovL3B1cmwub3JnL2RjL2VsZW1lbnRzLzEuMS8iIHhtbG5zOnBob3Rvc2hvcD0iaHR0cDovL25zLmFkb2JlLmNvbS9waG90b3Nob3AvMS4wLyIgeG1wOkNyZWF0b3JUb29sPSJBZG9iZSBQaG90b3Nob3AgQ0MgMjAxOSAoV2luZG93cykiIHhtcDpDcmVhdGVEYXRlPSIyMDIwLTA0LTI2VDAzOjE4OjI0KzAzOjAwIiB4bXA6TWV0YWRhdGFEYXRlPSIyMDIwLTA0LTI2VDAzOjE4OjI0KzAzOjAwIiB4bXA6TW9kaWZ5RGF0ZT0iMjAyMC0wNC0yNlQwMzoxODoyNCswMzowMCIgeG1wTU06SW5zdGFuY2VJRD0ieG1wLmlpZDo0ODA3NmM4NC1hMTI2LTExNGMtYmQ0ZC02MjgwYTA0NzQ0MDIiIHhtcE1NOkRvY3VtZW50SUQ9ImFkb2JlOmRvY2lkOnBob3Rvc2hvcDplOGQ1YmYxNy1hZDUyLWMxNGMtYjVhMC1lYmRiYzY1NjMyM2IiIHhtcE1NOk9yaWdpbmFsRG9jdW1lbnRJRD0ieG1wLmRpZDo0M2JkNDFkOC1kMDcxLWY3NDctYjg4YS05NDc1MmIwOWE1YzkiIGRjOmZvcm1hdD0iaW1hZ2UvcG5nIiBwaG90b3Nob3A6Q29sb3JNb2RlPSIxIj4gPHhtcE1NOkhpc3Rvcnk+IDxyZGY6U2VxPiA8cmRmOmxpIHN0RXZ0OmFjdGlvbj0iY3JlYXRlZCIgc3RFdnQ6aW5zdGFuY2VJRD0ieG1wLmlpZDo0M2JkNDFkOC1kMDcxLWY3NDctYjg4YS05NDc1MmIwOWE1YzkiIHN0RXZ0OndoZW49IjIwMjAtMDQtMjZUMDM6MTg6MjQrMDM6MDAiIHN0RXZ0OnNvZnR3YXJlQWdlbnQ9IkFkb2JlIFBob3Rvc2hvcCBDQyAyMDE5IChXaW5kb3dzKSIvPiA8cmRmOmxpIHN0RXZ0OmFjdGlvbj0ic2F2ZWQiIHN0RXZ0Omluc3RhbmNlSUQ9InhtcC5paWQ6NDgwNzZjODQtYTEyNi0xMTRjLWJkNGQtNjI4MGEwNDc0NDAyIiBzdEV2dDp3aGVuPSIyMDIwLTA0LTI2VDAzOjE4OjI0KzAzOjAwIiBzdEV2dDpzb2Z0d2FyZUFnZW50PSJBZG9iZSBQaG90b3Nob3AgQ0MgMjAxOSAoV2luZG93cykiIHN0RXZ0OmNoYW5nZWQ9Ii8iLz4gPC9yZGY6U2VxPiA8L3htcE1NOkhpc3Rvcnk+IDwvcmRmOkRlc2NyaXB0aW9uPiA8L3JkZjpSREY+IDwveDp4bXBtZXRhPiA8P3hwYWNrZXQgZW5kPSJyIj8+/G3t+AAAAKxJREFUKM9j/M9AAQBqTgYS9xiyGKSJ0iDMkMhwEaij5j9Ysx3DFiAFgssYHPBqtGCYBVW5l8EVohkEJBhKGO6DhZ8yVDDIY2iTYMhjuAOWf8FQB5FHaIYAa4aFUJO3M/jCRV0Y1kBFVzM4o3gYI8DEGVIYLoGVvmOoZShjeAJm32HIYZDBCC0coW3NMAVq23+Gucj2IWtmBCJcwSPIkMDAxrCA4SWueGKkJJ4BRj5Dv41LbO0AAAAASUVORK5CYII="
                                            alt="Toggle arrow"
                                            w="30px"
                                            h={pxToRem(9)}
                                        />
                                    </Button>
                                </Flex>
                                {expandedYears.has(year) && (
                                    <Stack spacing={0}>
                                        {archiveMenu[year].map((month) => (
                                            <Button
                                                key={month}
                                                variant="ghost"
                                                justifyContent={{base: 'center', lg: 'flex-start'}}
                                                sx={
                                                    selectedMonth === month && selectedYear === year
                                                        ? monthActiveStyle
                                                        : monthStyle
                                                }
                                                onClick={() => {
                                                    setSelectedMonth(month)
                                                    setSelectedYear(year)
                                                    setActivePage(0)
                                                    setExpandedYears(new Set())
                                                    const monthNumber =
                                                        typeof month === 'string' && isNaN(month)
                                                            ? getMonthNumber(month)
                                                            : month
                                                    const monthParam = `${year}-${monthNumber
                                                        .toString()
                                                        .padStart(2, '0')}`
                                                    updateURLParams('', monthParam)
                                                }}
                                            >
                                                {month}
                                            </Button>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        ))}
                    <Box sx={{w: '100%', h: pxToRem(0.5), bg: 'cav_pdp_light_gray'}} />
                </Box>
                <Flex gap={0} align="flex-start" mt={pxToRem(21)}>
                    {/* First Row */}
                    <Box
                        flex="3"
                        borderRight={
                            pageSize > 10 && {
                                base: 'none',
                                lg: `${pxToRem(0.5)} solid ${colors.cav_pdp_light_gray}`
                            }
                        }
                    >
                        <Flex
                            flexDir={{base: 'column', md: 'row'}}
                            height={{md: pxToRem(84)}}
                            w="auto"
                            p={{base: pxToRem(6), md: 0}}
                            pb={{base: pxToRem(11), md: 0}}
                            gap={{base: pxToRem(40), md: 0}}
                            justify="space-between"
                        >
                            <Select
                                width={{md: pxToRem(217), xl: pxToRem(205)}}
                                value={sortRule}
                                onChange={(e) => {
                                    setSortRule(e.target.value)
                                    setActivePage(0)
                                    updatePaginationURL(0, pageSize)
                                }}
                                sx={selectStyle}
                            >
                                {sortOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                            <Flex gap={{md: pxToRem(15), lg: pxToRem(20)}}>
                                <Select
                                    display={{base: 'none', md: 'block'}}
                                    width={{md: pxToRem(168)}}
                                    value={pageSize}
                                    onChange={(e) => {
                                        const newSize = parseInt(e.target.value)
                                        setPageSize(newSize)
                                        setActivePage(0)
                                        updatePaginationURL(0, newSize)
                                    }}
                                    sx={selectStyle}
                                    mr={maxPages > 1 ? 0 : pxToRem(20)}
                                >
                                    {pageOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Select>
                                {maxPages > 1 && (
                                    <>
                                        {maxPages < 3 ? (
                                            <Flex
                                                flexDir="row"
                                                justify="center"
                                                sx={dropdownBoxSmallStyle}
                                            >
                                                <Flex gap={{xl: pxToRem(20)}}>
                                                    {visiblePages.map((pageIndex) => (
                                                        <Button
                                                            key={pageIndex}
                                                            variant="ghost"
                                                            sx={
                                                                activePage === pageIndex
                                                                    ? paginationButtonActiveStyle
                                                                    : paginationButtonStyle
                                                            }
                                                            onClick={() => {
                                                                setActivePage(pageIndex)
                                                                updatePaginationURL(
                                                                    pageIndex,
                                                                    pageSize
                                                                )
                                                            }}
                                                        >
                                                            {pageIndex + 1}
                                                        </Button>
                                                    ))}
                                                </Flex>
                                            </Flex>
                                        ) : (
                                            <Flex
                                                sx={dropdownBoxStyle}
                                                justify="center"
                                                mr={{md: pxToRem(20)}}
                                            >
                                                <Flex align="center" gap={pxToRem(20)}>
                                                    {arrowVisible && activePage > 0 && (
                                                        <Button
                                                            variant="ghost"
                                                            sx={{
                                                                ...paginationButtonStyle,
                                                                textDecoration: 'none'
                                                            }}
                                                            onClick={() => {
                                                                const newPage = activePage - 1
                                                                setActivePage(newPage)
                                                                updatePaginationURL(
                                                                    newPage,
                                                                    pageSize
                                                                )
                                                            }}
                                                        >
                                                            &lt;
                                                        </Button>
                                                    )}
                                                    {visiblePages.map((pageIndex) => (
                                                        <Button
                                                            key={pageIndex}
                                                            variant="ghost"
                                                            sx={
                                                                activePage === pageIndex
                                                                    ? paginationButtonActiveStyle
                                                                    : paginationButtonStyle
                                                            }
                                                            onClick={() => {
                                                                setActivePage(pageIndex)
                                                                updatePaginationURL(
                                                                    pageIndex,
                                                                    pageSize
                                                                )
                                                            }}
                                                        >
                                                            {pageIndex + 1}
                                                        </Button>
                                                    ))}
                                                    {arrowVisible && activePage < maxPages - 1 && (
                                                        <Button
                                                            variant="ghost"
                                                            sx={{
                                                                ...paginationButtonStyle,
                                                                textDecoration: 'none'
                                                            }}
                                                            onClick={() => {
                                                                const newPage = activePage + 1
                                                                setActivePage(newPage)
                                                                updatePaginationURL(
                                                                    newPage,
                                                                    pageSize
                                                                )
                                                            }}
                                                        >
                                                            &gt;
                                                        </Button>
                                                    )}
                                                </Flex>
                                            </Flex>
                                        )}
                                    </>
                                )}
                            </Flex>
                        </Flex>
                        <Ticket articles={articles} />
                    </Box>
                    {/* Second Row */}
                    <Box
                        display={{base: 'none', lg: 'block'}}
                        flex="1"
                        sx={secondRowStyle}
                        borderLeft={
                            pageSize <= 10 && {
                                base: 'none',
                                lg: `${pxToRem(0.5)} solid ${colors.cav_pdp_light_gray}`
                            }
                        }
                    >
                        <Button
                            sx={{...clearButtonStyle, mb: {md: pxToRem(15)}}}
                            onClick={() => {
                                setSelectedYear('')
                                setSelectedMonth('')
                                setActivePage(0)
                                history.push({
                                    pathname: location.pathname,
                                    search: ''
                                })
                            }}
                        >
                            {intl.formatMessage({
                                id: 'bloglist.clear_date_filter',
                                defaultMessage: `Clear Date Filter`
                            })}
                        </Button>
                        {Object.keys(archiveMenu || {})
                            .sort((a, b) => b - a)
                            .map((year, index) => (
                                <Box key={year}>
                                    <Button
                                        variant="ghost"
                                        sx={
                                            selectedYear === year && selectedMonth === ''
                                                ? yearActiveStyle
                                                : yearStyle
                                        }
                                        borderTop={
                                            index &&
                                            `${pxToRem(1)} solid ${colors.cav_pdp_light_gray}`
                                        }
                                        borderBottom={`${pxToRem(1)} solid ${
                                            colors.cav_pdp_light_gray
                                        }`}
                                        onClick={() => {
                                            setSelectedYear(year)
                                            setSelectedMonth('')
                                            setActivePage(0)
                                            const newParams = new URLSearchParams()
                                            newParams.set('year', year)
                                            history.push({
                                                pathname: location.pathname,
                                                search: newParams.toString()
                                            })
                                        }}
                                    >
                                        {year}
                                    </Button>
                                    <Stack spacing={0}>
                                        {archiveMenu[year].map((month) => (
                                            <Button
                                                key={month}
                                                variant="ghost"
                                                justifyContent="flex-start"
                                                sx={
                                                    selectedMonth === month && selectedYear === year
                                                        ? monthActiveStyle
                                                        : monthStyle
                                                }
                                                onClick={() => {
                                                    setSelectedMonth(month)
                                                    setSelectedYear(year)
                                                    setActivePage(0)
                                                    setExpandedYears(new Set())
                                                    const monthNumber =
                                                        typeof month === 'string' && isNaN(month)
                                                            ? getMonthNumber(month)
                                                            : month
                                                    const monthParam = `${year}-${monthNumber
                                                        .toString()
                                                        .padStart(2, '0')}`
                                                    updateURLParams('', monthParam)
                                                }}
                                            >
                                                {month}
                                            </Button>
                                        ))}
                                    </Stack>
                                </Box>
                            ))}
                        <Box sx={{w: '100%', h: pxToRem(0.5), bg: 'cav_pdp_light_gray'}} />
                    </Box>
                </Flex>
            </Stack>
        )
    )
}

BlogArchive.propTypes = {
    onFilterChange: PropTypes.func
}

export default BlogArchive
